import * as React from "react";
import { Stack, PrimaryButton, Spinner, StackItem, Label, ILabelStyles, FontSizes } from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { RootState } from "../../redux";
import { useSelector } from "react-redux";
import { ModeEnum } from "../enums/modeEnum";
import { getPlatform } from "../../styles/mobileStyles";
import generalModule from "../../../../utilities/GeneralModule";
import { AfterSaveAppSelector } from "./AfterSaveAppSelector";
import { IFileSaveResult } from "../_v2/redux/api/local/api-local-interfaces";
import { GetOfficeContextApp } from "../helpers/officeHelper";
import { SaveMode } from "../enums/SaveModeEnum";
import savingLocationStructureModule from "../../../../utilities/SavingLocationStructureModule";
import { ComponentTypes } from "../../config/config";
import { tryDecodeUriComponent } from "../../../../utilities/EncodeDecode";

interface ISavingInBackgroundContentProps {
  dismissPanel: () => void;
  saveResult?: IFileSaveResult;
  isFileSystemFileUpload?: boolean;
  saveMode: SaveMode
}

const fileNameStyles: ILabelStyles = {
  root: {
    fontSize: FontSizes.size16
  }
}

const pathStyles: ILabelStyles = {
  root: {
    fontSize: FontSizes.size12,
    fontWeight: 400
  }
}

const headingStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: "bold"
}

const progressStyle: React.CSSProperties = {
  display: "flex",
  height: 35,
  justifyContent: "center"
}

export const SavingInBackgroundContent = ({ dismissPanel, saveResult, isFileSystemFileUpload, saveMode }: ISavingInBackgroundContentProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const isSuccess = useSelector((state: RootState) => state.savingInBackground.isSuccess);
  const errorMessages = useSelector((state: RootState) => state.savingInBackground.errorMessages);
  const mode = useSelector((state: RootState) => state.mode.mode);
  const savingLocation = useSelector((state: RootState) => state.savingLocation);
  const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);

  const [saveLocationText, setSaveLocationText] = React.useState<string>();

  React.useEffect(() => {
    if (isSuccess || saveResult?.success) {
      if (mode === ModeEnum.office) {
        let savingStructure = workingDirectories?.length > 0 ? workingDirectories : savingLocationStructureModule.getSavingLocationStructure();
        if (savingStructure?.length > 0 && savingLocation) {
          if (savingStructure.length > 1 && savingStructure[savingStructure.length - 1].type === ComponentTypes.Saving) {
            savingStructure = savingStructure.slice(0, -1);
          }
          const finalSavingLocationDetails: string[] = savingStructure.slice(0, 2).map(s => s.displayName);
          if (savingLocation) {
            const savingDirectoryList: string[] = savingLocation.directory?.split("/") ?? [];
            if (savingLocation.documentLibrary) {
              finalSavingLocationDetails.push(savingDirectoryList.length > 1 ? savingLocation.documentLibrary : savingLocation.display);
            }
            if (savingDirectoryList.length > 1) {
              savingDirectoryList.slice(1).forEach(x => finalSavingLocationDetails.push(tryDecodeUriComponent(x)));
            }
            setSaveLocationText(finalSavingLocationDetails?.join(" > "));
          }
        }
      }
    }
  }, [saveResult, isSuccess, mode, workingDirectories, savingLocation]);

  const getFileSaveResultText = React.useCallback(() => {
    if (isSuccess || saveResult?.success) {
      if (mode === ModeEnum.office) {
        return languageStrings.UploadCompletedSuccessfully;
      } else {
        return languageStrings.Success;
      }
    } else {
      return languageStrings.FileBackgroundSaveFailure;
    }
  }, [saveResult, isSuccess]);

  const savingProgressMarkup = isBusySaving ? (
    <div style={{ marginTop: 75 }}>
      <div style={progressStyle}>
        <Spinner ariaLive="assertive" labelPosition="top" />
      </div>
      <p className="centerText">{mode !== ModeEnum.mailCompose ? languageStrings.FileBackgroundSave : languageStrings.MessageComposeSaving}</p>
    </div>
  ) : (
    <div style={mode === ModeEnum.office && !isFileSystemFileUpload ? { marginTop: 25 } : { marginTop: 75 }}>
      <div style={progressStyle}>
        <Label style={headingStyle}>{getFileSaveResultText()}</Label>
      </div>
      {errorMessages?.length ? (
        <>
          <div>{languageStrings.FilesNotSuccessfullyUploaded}</div>
          <ul>{errorMessages?.map((item, index) => (<li key={index}><span style={{ color: "red" }}>{item}</span></li>))}</ul>
        </>
      ) : null}
      {(mode !== ModeEnum.office || isFileSystemFileUpload)
        && <p className="centerText">
          {isSuccess ? (mode !== ModeEnum.mailCompose ? languageStrings.FileSaveSuccess : languageStrings.MessageComposeSaveSuccess) : languageStrings.RetryAfter}
        </p>}
      {mode === ModeEnum.office && !isFileSystemFileUpload && (!errorMessages || errorMessages?.length === 0) &&
        <>
          <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign="start" verticalAlign="center" style={{ paddingTop: 5 }}>
            <StackItem>{saveMode === SaveMode.Default
              ? <img
                src={`https://res-1.cdn.office.net/files/fabric-cdn-prod_20221209.001/assets/brand-icons/product/svg/${GetOfficeContextApp()?.name?.toLowerCase()}_48x1.svg`}
                width="48"
                height="48" />
              : <img
                src="https://documentcloud.adobe.com/spodintegration/filehandler-resources/file-handler-icon.svg"
                width="48"
                height="48" />}
            </StackItem>
            <StackItem>{saveMode === SaveMode.Default ? <Label styles={fileNameStyles}>{saveResult?.succeededFiles[0]?.name}</Label> : <Label styles={fileNameStyles}>{`${saveResult?.succeededFiles[0]?.name.substring(0, saveResult?.succeededFiles[0]?.name.lastIndexOf('.'))}.pdf`}</Label>}</StackItem>
          </Stack>
          {saveLocationText && <StackItem style={{ marginBottom: 15 }}><Label styles={pathStyles}>{saveLocationText}</Label></StackItem>}
          <AfterSaveAppSelector onSaveLocation={dismissPanel} saveResult={saveResult} saveMode={saveMode} />
        </>
      }
    </div>
  );
  return (
    <>
      {savingProgressMarkup}
      <Stack>
        {((mode !== ModeEnum.mailCompose && mode !== ModeEnum.office) || isFileSystemFileUpload) && <PrimaryButton text={languageStrings.Continue} allowDisabledFocus onClick={dismissPanel} className={!generalModule.isInConentMangerMode() && getPlatform == Office.PlatformType.iOS ? "xb-button-save ios-clean-button" : "xb-button-save"} />}
      </Stack>
    </>
  );
};
